import * as axios from 'axios'
import OrderRequest from '../../order-wizard/models/order/OrderRequest'
import { CustomField } from '../../order-wizard/models/CustomField'
import { cache } from '../decorators/cache.decorator'
import { ProductGroup } from '../../order-wizard/models/ProductGroup'
import { ProductConfig } from '../../order-wizard/models/ProductConfig'
import { clone, getBaseURL } from '../utils/common'

const root = '/api/webshop'
const SETUP_INVOICE_ITEM_NAME = 'SETUP'

class WebshopService {
  productGroups = null;

  async checkSimplePayResponse ({ simplePayResponse, simplePaySignature }) {
    const response = await axios.get(`${root}/checkBankCardPayment`, { params: { r: simplePayResponse, s: simplePaySignature } })
    return response.data
  }

  async getInvoice ({ invoiceId, key }) {
    let response = await axios.get(`${root}/invoice/${invoiceId}/${key}`)
    const productGroups = await this._queryProducts()
    let packageCodes = new Set()
    productGroups.forEach(productGroup => {
      productGroup.loyaltyPackages.forEach(loyaltyPackage => {
        loyaltyPackage.products.forEach(pack => {
          packageCodes.add(pack.code)
        })
      })
    })
    response.data.invoice.items = response.data.invoice.items.map(item => {
      let newItem = { ...item }
      if (!packageCodes.has(item.serviceName)) {
        newItem.description = ''
      } else if (packageCodes.has(item.serviceName) && item.description === SETUP_INVOICE_ITEM_NAME) {
        newItem.description = ''
        newItem.serviceName = SETUP_INVOICE_ITEM_NAME
      }
      return newItem
    })
    return response.data
  }

  checkEmail (email) {
    return new Promise((resolve, reject) => {
      axios.post(`${root}/checkEmail`, { email, category: 'FW' }).then(response => {
        if (response.data.result === 'USER_NOT_FOUND') {
          resolve(false)
        } else {
          resolve(true)
        }
      }).catch(e => {
        reject(e.response.data)
      })
    })
  }

  postOrder ({ contractData, cartProducts, billingCycle, loyaltyYears, userData }) {
    return new Promise((resolve, reject) => {
      axios.post(`${root}/order`, orderRequestConverter({ contractData, cartProducts, billingCycle, loyaltyYears, userData })).then(response => {
        resolve(response.data)
      }).catch(e => {
        reject(e.response.data)
      })
    })
  }

  getContractUrl ({ id, key }) {
    return `${getBaseURL()}/${root}/contract/${id}/${key}`.replace(/\/+/g, '/')
  }

  @cache
  async _queryProducts () {
    const productsQueryResult = await axios.get(`${root}/products/FW`)
    return productsQueryResult.data.groups
  }

  async getProductConfig () {
    if (this.productGroups !== null) {
      return this.productGroups
    }

    let groups = await this._queryProducts()
    this.productGroups = transformProductConfiguration(groups)
    return this.productGroups
  }
}

export const webshopService = new WebshopService()

function orderRequestConverter ({ contractData, cartProducts, billingCycle, loyaltyYears, userData }) {
  return new OrderRequest({
    client: {
      email: contractData.contactEmail,
      password: userData.password1,
      company: contractData.name,
      firstName: contractData.contactFirstName,
      lastName: contractData.contactLastName,
      phoneNumber: contractData.contactPhone,
      zipCode: contractData.zip,
      taxId: contractData.taxNbr,
      city: contractData.city,
      address: contractData.street,
      customFields: clientCustomFieldConverter({ ...contractData })
    },
    cart: {
      items: (() => {
        const products = clone(cartProducts).reduce((result, e) => {
          // we add as many products as its quantity
          let arr = []
          for (let i = 0; i < e.quantity; i++) {
            const addons = e.addons.map(a => { a.quantity = 1; return a })
            const customFields = serviceCustomFieldConverter(e)
            arr.push({
              id: e.id,
              quantity: 1,
              addons,
              customFields
            })
          }
          return result.concat(arr)
        }, [])
        return products
      })()
    },
    billingCycle,
    loyaltyYears,
    paymentMethod: userData.paymentMethod
  })
}

function clientCustomFieldConverter ({ representativeFirstName, representativeLastName, representativePhone, invoiceEmail }) {
  return [
    new CustomField({
      code: 'OFFICIAL_REPRESENTATIVE_FIRST_NAME',
      value: representativeFirstName
    }),
    new CustomField({
      code: 'OFFICIAL_REPRESENTATIVE_LAST_NAME',
      value: representativeLastName
    }),
    new CustomField({
      code: 'OFFICIAL_REPRESENTATIVE_PHONE',
      value: representativePhone
    }),
    new CustomField({
      code: 'INVOICE_EMAIL',
      value: invoiceEmail
    })
  ]
}

function serviceCustomFieldConverter ({ $$vehicleDetails }) {
  if (!$$vehicleDetails) return []
  const details = $$vehicleDetails.shift()
  if (!details) return []
  return [
    new CustomField({
      code: 'PRODUCT_PRODUCTION_YEAR',
      value: details.productionYear || ''
    }),
    new CustomField({
      code: 'PRODUCT_VEHICLE_BRAND',
      value: details.vehicleBrand || ''
    }),
    new CustomField({
      code: 'PRODUCT_VEHICLE_TYPE',
      value: details.vehicleType || ''
    }),
    new CustomField({
      code: 'PRODUCT_LICENCE_PLATE',
      value: details.licencePlate || ''
    })
  ]
}

function transformProductConfiguration (groups) {
  let productGroups = []
  let possibleLoyalties = []

  groups.forEach(group => {
    let packages = []

    group.loyaltyPackages && group.loyaltyPackages.forEach(loyaltyPackage => {
      if (possibleLoyalties.indexOf(loyaltyPackage.loyaltyLength) === -1) {
        possibleLoyalties.push(loyaltyPackage.loyaltyLength)
      }

      loyaltyPackage.products.forEach(product => {
        packages.push({
          ...product,
          loyaltyLength: loyaltyPackage.loyaltyLength
        })
      })
    })

    productGroups.push(new ProductGroup({
      ...group,
      packages
    }))
  })

  return new ProductConfig({ possibleLoyalties, productGroups })
}
